import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Converts a URL-safe base64 VAPID public key to a Uint8Array
 * required by the Push API's applicationServerKey.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Hook that manages Web Push subscription lifecycle:
 * - Requests notification permission
 * - Fetches VAPID public key from server
 * - Subscribes via PushManager
 * - Sends subscription to server
 * - Handles unsubscribe
 *
 * Only runs when `enabled` is true (i.e. user is logged in as patient).
 */
export function usePushNotifications({ token, enabled = false }) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const subscriptionRef = useRef(null);

  // Check if push is supported in this browser
  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const getVapidKey = useCallback(async () => {
    const response = await axios.get('/api/push/vapid-public-key');
    if (!response.data?.publicKey) throw new Error('VAPID key not available');
    return response.data.publicKey;
  }, []);

  const saveSubscriptionToServer = useCallback(
    async (subscription) => {
      if (!token) return;
      const sub = subscription.toJSON();
      await axios.post(
        '/api/push/subscribe',
        {
          endpoint: sub.endpoint,
          keys: sub.keys,
          userAgent: navigator.userAgent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    [token]
  );

  const subscribe = useCallback(async () => {
    if (!isSupported || !enabled) return;
    setLoading(true);
    try {
      // 1. Request permission
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        setLoading(false);
        return;
      }

      // 2. Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // 3. Get existing subscription or create new one
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const vapidKey = await getVapidKey();
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }

      subscriptionRef.current = subscription;

      // 4. Send to server
      await saveSubscriptionToServer(subscription);
      setSubscribed(true);
    } catch (err) {
      console.error('[Push] Subscribe failed:', err);
    } finally {
      setLoading(false);
    }
  }, [isSupported, enabled, getVapidKey, saveSubscriptionToServer]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await axios
          .delete('/api/push/unsubscribe', {
            data: { endpoint: subscription.endpoint },
            headers: { Authorization: `Bearer ${token}` }
          })
          .catch(() => {});
        await subscription.unsubscribe();
      }
      subscriptionRef.current = null;
      setSubscribed(false);
    } catch (err) {
      console.error('[Push] Unsubscribe failed:', err);
    } finally {
      setLoading(false);
    }
  }, [isSupported, token]);

  // Auto-subscribe when user is logged in and has previously granted permission
  useEffect(() => {
    if (!enabled || !isSupported || !token) return;

    let cancelled = false;

    const autoSubscribe = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSub = await registration.pushManager.getSubscription();

        if (existingSub && !cancelled) {
          subscriptionRef.current = existingSub;
          // Refresh subscription on server (endpoint may have rotated)
          await saveSubscriptionToServer(existingSub).catch(() => {});
          setSubscribed(true);
        } else if (Notification.permission === 'granted' && !existingSub && !cancelled) {
          // Permission was already granted, silently re-subscribe
          await subscribe();
        }
      } catch (err) {
        console.warn('[Push] Auto-subscribe check failed:', err);
      }
    };

    autoSubscribe();
    return () => { cancelled = true; };
  }, [enabled, isSupported, token, saveSubscriptionToServer, subscribe]);

  return {
    isSupported,
    permission,
    subscribed,
    loading,
    subscribe,
    unsubscribe
  };
}
